import React, { useState, useEffect } from 'react';
import { ValidatedForm, ValidatedField, translate, Storage, Translate } from 'react-jhipster';
import { Button, Label } from 'reactstrap';
import { Grid, Divider, Typography, FormGroup, FormControlLabel, Checkbox, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneInput from 'react-phone-input-2';
import { createTheme, responsiveFontSizes, ThemeProvider } from '@mui/material/styles';
import { countryList } from 'app/entities/bank/country';
import { AUTHORITIES } from 'app/config/constants';
import Box from '@mui/material/Box';
import { QuotationPrint } from 'app/entities/quotation/bought/components/quotation-print';
import { QuotationPrint as BoubyanQuotationPrint } from 'app/entities/quotation/client-docs/boubyan-bank/quotation/quotation-print';
import { useAppSelector } from 'app/config/store';
import moment from 'moment';
import { getLocalPrayerTimeEnd, getLocalPrayerTimeStart } from 'app/shared/util/date-utils';
import { isBoubyanQuotation } from 'app/shared/util/quotation-utils';

const QuotationBoughtForm = ({
  quotationEntity,
  poNumberData,
  poCreatedDateData,
  currencyNameData,
  amountData,
  commodityNameData,
  descriptionData,
  itemDescriptionInArabic,
  commodityUnitPriceData,
  commodityQuantityData,
  commodityUnitData,
  transactionFeeData,
  feeInQuotationCurrencyData,
  totalAmountData,
  customerICData,
  customerNameData,
  customerNameArData,
  customerTelData,
  customerEmailData,
  branchCustomerApproval,
  handleToSubmit,
  isLocalCommodity,
  operationTime,
  comexSettingsEntity,
  shouldRenderButton,
  handleAmendClick,
  transferAndKeepSellButtons,
  reInitiateButtons,
  manualOrderRequestBtn,
  purchaseCancellation,
  renderIban,
  isDefaultCurrency,
  account,
  checkedCustomerApproval,
  handleCustomerApproval,
  resendSmsButtons,
  localStart,
  localEnd,
  serverStatus,
}) => {
  const bankStaffAccount = useAppSelector(state => state.userManagement.bankStaff);

  const [errCustApprovalMsg, setErrCustApprovalRoleMsg] = useState('');
  const [customerNationalityData, setCustomerNationalityData] = useState('');

  const handlePrint = () => {
    const printContent = document.getElementById('divToPrint').innerHTML;
    // Get all loaded stylesheet URLs
    const styleSheets = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return styleSheet.href
            ? `<link rel="stylesheet" type="text/css" href="${styleSheet.href}" />`
            : `<style>${Array.from(styleSheet.cssRules)
                .map(rule => rule.cssText)
                .join('\n')}</style>`;
        } catch (e) {
          return '';
        }
      })
      .filter(Boolean)
      .join('\n');

    const locale = Storage.session.get('locale');
    const isArabic = locale === 'ar-ly';

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
      <html>
        <head>
          ${styleSheets}
          <style>
            body {
              ${isArabic ? "font-family: 'Amiri', serif; direction: rtl;" : ''}
            }
          </style>
        </head>
        <body>
          <div id="printContainer">${printContent}</div>
          <script>
            window.onload = function() {
              setTimeout(() => {
                window.print();
                window.close();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
      // Wait for stylesheets to load
      const loadPromises = Array.from(newWindow.document.getElementsByTagName('link')).map(
        link =>
          new Promise(resolve => {
            link.onload = resolve;
            link.onerror = resolve; // Continue even if some styles fail to load
          })
      );

      Promise.all(loadPromises).then(() => {
        setTimeout(() => {
          newWindow.print();
          newWindow.close();
        }, 500);
      });

      newWindow.document.close();
    }
  };

  const isLockedByCurrentUser = (): boolean => {
    const isLockedByMe = quotationEntity?.isTransactionLocked === true && quotationEntity?.lockedByShserId === account?.id;
    const isUnlockedAndNoOwner = quotationEntity?.isTransactionLocked === null && quotationEntity?.lockedByShserId === null;

    return isLockedByMe || isUnlockedAndNoOwner;
  };

  const getShouldDisable = (): boolean => {
    const isDecisionMade = quotationEntity?.comexAdminDecision !== null;
    const isNotLocked = quotationEntity?.isTransactionLocked === false;
    return isDecisionMade || isNotLocked || !isLockedByCurrentUser();
  };

  const operationTimeError = () => {
    if (operationTime === false) {
      return (
        <span style={{ color: 'red' }}>
          {isLocalCommodity !== undefined && !isLocalCommodity ? (
            <Translate
              contentKey="home.fridayPrayerMessage"
              interpolate={{ startTime: getLocalPrayerTimeStart(), endTime: getLocalPrayerTimeEnd() }}
            />
          ) : (
            <Translate
              contentKey="home.operationTime"
              interpolate={{
                startTime: moment(localStart).tz(moment.tz.guess()).format('hh:mm A'),
                endTime: moment(localEnd).tz(moment.tz.guess()).format('hh:mm A'),
              }}
            />
          )}
        </span>
      );
    } else {
      return <></>;
    }
  };

  // Update errCustApprovalMsg based on the locale changes
  useEffect(() => {
    if (errCustApprovalMsg) {
      setErrCustApprovalRoleMsg(translate('comexApp.quotation.customerApproval.errorMessage'));
    }
  }, [Storage.session.get('locale'), errCustApprovalMsg]);

  useEffect(() => {
    setCustomerNationalityData(quotationEntity?.customer?.nationality || '');
  }, []);

  // Tooltip validation logic
  const isCustomerApprovalDisabled =
    !account?.authorities?.includes(AUTHORITIES.BANK_STAFF) ||
    (isLocalCommodity ? quotationEntity?.localCommodityBidCert === null : quotationEntity?.bursaAPIBidCertId === null);

  const validateForm = () => {
    if (checkedCustomerApproval === false && branchCustomerApproval === true) {
      setErrCustApprovalRoleMsg(translate('comexApp.quotation.customerApproval.errorMessage'));
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      handleToSubmit();
    }
  };

  const selectedNationality = countryList?.find(c => c?.Desc_en === customerNationalityData);

  let responsiveFont = createTheme();
  responsiveFont = responsiveFontSizes(responsiveFont);

  const renderCustomerNameSection = () => (
    <div className="form-group row" style={{ alignItems: 'center' }}>
      <div className="col-sm-3">
        <Label>{translate('comexApp.quotation.customer')}</Label>
      </div>
      <div className="col-sm-9 box-space">
        <ValidatedField id="cust_name" name="cust_name" data-cy="cust_name" type="text" disabled value={customerNameData} />

        <ValidatedField id="cust_name_ar" name="cust_name_ar" data-cy="cust_name_ar" type="text" disabled value={customerNameArData} />
      </div>
    </div>
  );

  const TransactionFeeField = ({ label, currencyNameLabel, feeValue }) => (
    <div
      className="form-group row"
      style={{ alignItems: 'center' }}
      hidden={quotationEntity?.bank?.displayFeeInTransactionCurrency === false}
    >
      <div className="col-sm-3">
        <Label>{translate(label)}</Label>
      </div>
      <div
        className="row col-sm-9"
        style={
          Storage.session.get('locale') === 'en'
            ? { paddingRight: '0px', paddingLeft: '24px' }
            : { paddingRight: '24px', paddingLeft: '0px' }
        }
      >
        <div className="col-5 col-sm-3 px-0 box-space">
          <ValidatedField
            id={`${currencyNameLabel}_currency_name`}
            name={currencyNameLabel}
            data-cy={currencyNameLabel}
            type="text"
            value={currencyNameData}
            disabled={true}
          />
        </div>
        <div className="col-7 col-sm-9 px-0 box-space">
          <ValidatedField
            id={`${currencyNameLabel}_fee`}
            name={`${currencyNameLabel}_fee`}
            data-cy={`${currencyNameLabel}_fee`}
            value={feeValue}
            disabled={true}
            style={{
              textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right',
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <ValidatedForm onSubmit={handleSubmit}>
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.purchaseOrderNo')}</Label>
        </div>
        <div className="col-sm-9 box-space">
          <ValidatedField
            id="po_number"
            name="po_number"
            data-cy="po_number"
            // type="number"
            type="text"
            value={poNumberData}
            disabled
          />
        </div>
      </div>
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.date')}</Label>
        </div>
        <div className="col-sm-9 box-space">
          <ValidatedField
            id="date"
            name="date"
            data-cy="date"
            type="text" // date
            disabled
            value={poCreatedDateData}
            style={{
              textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right',
            }}
          />
        </div>
      </div>
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.amount')}</Label>
        </div>
        <div
          className="row col-sm-9"
          style={
            Storage.session.get('locale') === 'en'
              ? {
                  paddingRight: '0px',
                  paddingLeft: '24px',
                }
              : { paddingRight: '24px', paddingLeft: '0px' }
          }
        >
          <div className="col-5 col-sm-3 px-0 box-space">
            <ValidatedField id="currencyName" name="currencyName" data-cy="currencyName" type="text" disabled value={currencyNameData} />
          </div>
          <div className="col-7 col-sm-9 px-0 box-space">
            <ValidatedField
              id="amount"
              name="amount"
              data-cy="amount"
              // type="number"
              // type={Storage.session.get('locale') === 'en' ? "number" : "text"}
              type={'text'}
              disabled
              value={amountData}
              style={{ textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right' }}
            />
          </div>
        </div>
      </div>
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.itemName')}</Label>
        </div>
        <div className="col-sm-9 box-space">
          <ValidatedField id="item_name" name="item_name" data-cy="item_name" type="text" disabled value={commodityNameData} />
        </div>
      </div>
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.description')}</Label>
        </div>
        <div className="col-sm-9 box-space">
          <ValidatedField
            id="description"
            name="description"
            data-cy="description"
            type="textarea"
            disabled
            value={Storage.session.get('locale') === 'en' ? descriptionData : itemDescriptionInArabic || descriptionData}
          />
        </div>
      </div>
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.price')}</Label>
        </div>
        <div
          className="row col-sm-9"
          style={
            Storage.session.get('locale') === 'en'
              ? {
                  paddingRight: '0px',
                  paddingLeft: '24px',
                }
              : { paddingRight: '24px', paddingLeft: '0px' }
          }
        >
          <div className="col-5 col-sm-3 px-0 box-space">
            <ValidatedField id="currencyName" name="currencyName" data-cy="currencyName" type="text" disabled value={currencyNameData} />
          </div>
          <div className="col-7 col-sm-9 px-0 box-space">
            <ValidatedField
              id="price"
              name="price"
              data-cy="price"
              type="text"
              // type={Storage.session.get('locale') === 'en' ? "number" : "text"}
              disabled
              value={commodityUnitPriceData}
              style={{ textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right' }}
            />
          </div>
        </div>
      </div>
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.quantity')}</Label>
        </div>
        <div
          className="row col-sm-9"
          style={
            Storage.session.get('locale') === 'en'
              ? { paddingRight: '0px', paddingLeft: '24px' }
              : { paddingRight: '24px', paddingLeft: '0px' }
          }
        >
          <div className="col-5 col-sm-3 px-0 box-space">
            <ValidatedField
              id="quantity_unit"
              name="quantity_unit"
              data-cy="quantity_unit"
              type="text"
              value={commodityUnitData}
              disabled={true}
            />
          </div>
          <div className="col-7 col-sm-9 px-0 box-space">
            <ValidatedField
              id="quantity"
              name="quantity"
              data-cy="quantity"
              type="text"
              // type={Storage.session.get('locale') === 'en' ? "number" : "text"}
              disabled
              value={commodityQuantityData}
              style={{
                textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right',
              }}
            />
          </div>
        </div>
      </div>
      <div className="form-group row" style={{ alignItems: 'center' }} hidden={quotationEntity?.bank?.displayTransactionFee === false}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.transactionFee')}</Label>
        </div>
        <div
          className="row col-sm-9"
          style={
            Storage.session.get('locale') === 'en'
              ? {
                  paddingRight: '0px',
                  paddingLeft: '24px',
                }
              : { paddingRight: '24px', paddingLeft: '0px' }
          }
        >
          <div className="col-5 col-sm-3 px-0 box-space">
            <ValidatedField
              id="currencyName"
              name="currencyName"
              data-cy="currencyName"
              type="text"
              disabled
              value={quotationEntity?.feeCurrencyIndex || ''}
            />
          </div>
          <div className="col-7 col-sm-9 px-0 box-space">
            <ValidatedField
              id="fee"
              name="fee"
              data-cy="fee"
              type="text"
              value={transactionFeeData}
              disabled
              style={{ textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right' }}
            />
          </div>
        </div>
      </div>
      {isDefaultCurrency && quotationEntity?.bank?.displayFeeInTransactionCurrency && (
        <TransactionFeeField
          label="comexApp.quotation.transactionFeeInQuotationCurrency"
          currencyNameLabel="quotation_currency_name_option"
          feeValue={feeInQuotationCurrencyData}
        />
      )}
      <div className="form-group row" style={{ alignItems: 'center' }} hidden>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.totalAmount')}</Label>
        </div>
        <div
          className="row col-sm-9"
          style={
            Storage.session.get('locale') === 'en'
              ? {
                  paddingRight: '0px',
                  paddingLeft: '24px',
                }
              : { paddingRight: '24px', paddingLeft: '0px' }
          }
        >
          <div className="col-5 col-sm-3 px-0 box-space">
            <ValidatedField id="currencyName" name="currencyName" data-cy="currencyName" type="text" disabled value={currencyNameData} />
          </div>
          <div className="col-7 col-sm-9 px-0 box-space">
            <ValidatedField
              id="totalAmount"
              name="totalAmount"
              data-cy="totalAmount"
              type="text"
              value={totalAmountData}
              disabled
              style={{ textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right' }}
            />
          </div>
        </div>
      </div>
      &nbsp;
      <Divider />
      &nbsp;
      <ThemeProvider theme={responsiveFont}>
        <Typography variant="h6" component="div">
          <strong>{translate('comexApp.quotation.requestedBy')}</strong>
        </Typography>
      </ThemeProvider>
      &nbsp;
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.nationalId')}</Label>
        </div>
        <div className="col-sm-9 box-space">
          <ValidatedField id="national_id" name="national_id" data-cy="national_id" type="text" disabled value={customerICData} />
        </div>
      </div>
      {renderCustomerNameSection()}
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.nationality')}</Label>
        </div>
        <div className="col-sm-9 box-space">
          <ValidatedField
            id="nationality"
            name="nationality"
            data-cy="nationality"
            type="text"
            disabled
            value={Storage.session.get('locale') === 'en' ? quotationEntity?.customer?.nationality : selectedNationality?.Desc_ar || ''}
          />
        </div>
      </div>
      <div className="form-group row mb-2" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.phoneNo')}</Label>
        </div>
        <div className="col-sm-9 box-space">
          <PhoneInput
            country={'us'}
            value={customerTelData}
            inputStyle={{
              marginBottom: '2%',
              textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right',
              direction: 'ltr',
            }}
            disabled={true}
            disableDropdown={true}
          />
        </div>
      </div>
      <div className="form-group row" style={{ alignItems: 'center' }}>
        <div className="col-sm-3">
          <Label>{translate('comexApp.quotation.email')}</Label>
        </div>
        <div className="col-sm-9 box-space">
          <ValidatedField id="email" name="email" data-cy="email" disabled value={customerEmailData} />
        </div>
      </div>
      {/* {renderIban()} */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6} style={{ display: 'inline-flex', justifyContent: 'flex-start' }}>
          <div style={{ paddingTop: '7px' }}>
            <Translate contentKey="comexApp.quotation.pendingManagerApproval">Pending Manager Approval</Translate> &nbsp;
            {quotationEntity?.bankManagerApproval === true && <CheckCircleIcon color="success" fontSize="small" />}
          </div>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          style={{
            display: 'inline-flex',
            justifyContent: 'flex-end',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'inline-flex', justifyContent: 'flex-end' }}>
            {quotationEntity?.bankCustomerApproval === false && branchCustomerApproval === true ? (
              <div
                style={{ display: 'flex', flexDirection: 'column' }}
                hidden={isLocalCommodity ? quotationEntity?.localCommodityBidCert === null : quotationEntity?.bursaAPIBidCertId === null}
              >
                <FormGroup style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <FormControlLabel
                    control={<Checkbox size="small" onChange={handleCustomerApproval} />}
                    label={translate('comexApp.quotation.customerApproval.title')}
                    value="customerApproval"
                    disabled={isCustomerApprovalDisabled}
                    className="m-0"
                  />
                  &nbsp;
                  <Tooltip title={translate('comexApp.quotation.customerApproval.tooltip')}>
                    <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'pointer' }} />
                  </Tooltip>
                </FormGroup>
                <Label
                  hidden={checkedCustomerApproval}
                  style={{
                    color: '#DC3545',
                    fontSize: 14,
                    fontWeight: 400,
                    margin: '0 12px 0 12px',
                  }}
                >
                  {errCustApprovalMsg}
                </Label>
              </div>
            ) : (
              <FormGroup hidden={branchCustomerApproval === false} style={{ flexDirection: 'row', alignItems: 'center' }}>
                <FormControlLabel
                  control={<Checkbox size="small" />}
                  label={translate('comexApp.quotation.customerApproval.title')}
                  disabled
                  checked
                  className="mx-0"
                />
                &nbsp;
                <Tooltip title={translate('comexApp.quotation.customerApproval.tooltip')}>
                  <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'pointer' }} />
                </Tooltip>
              </FormGroup>
            )}
          </div>

          <div className="col-sm-12" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {operationTime === true ? (
              shouldRenderButton() ? (
                <Button
                  color="primary"
                  id="save-entity"
                  data-cy="entityCreateSaveButton"
                  onClick={handleAmendClick}
                  disabled={getShouldDisable()}
                >
                  <Translate contentKey="comexApp.quotation.branchManagerApproveBtnLabel">Approve/Deny</Translate>
                </Button>
              ) : (
                <></>
              )
            ) : shouldRenderButton() ? (
              <>
                <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" disabled>
                  <Translate contentKey="comexApp.quotation.branchManagerApproveBtnLabel">Approve/Deny</Translate>
                </Button>
              </>
            ) : (
              <></>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
              <Box
                display="flex"
                gap={0.5}
                alignItems="center"
                justifyContent="flex-start"
                overflow="auto"
                whiteSpace="nowrap"
                maxWidth="100%"
                flex="1"
              >
                {transferAndKeepSellButtons()}
                {quotationEntity?.bursaAPIBidCertId === null && reInitiateButtons()}
                {(quotationEntity?.status === 'Bought' ||
                  quotationEntity?.status === 'Transferred' ||
                  quotationEntity?.status === 'Sold') &&
                  manualOrderRequestBtn()}
              </Box>
            </Box>

            {/* PRINT */}
            <div>
              <Button color="link" className="px-1" id="save-entity" data-cy="entityCreateSaveButton" onClick={handlePrint}>
                <Translate contentKey="comexApp.quotation.printBtnLabel">Print</Translate>
              </Button>
            </div>

            {quotationEntity?.cancelOrder === true &&
            purchaseCancellation.comexAdminApproval === null &&
            (account?.authorities?.includes(AUTHORITIES.ALSHMOKH_ADMIN) ||
              account?.authorities?.includes(AUTHORITIES.SUBADMIN_CANCELLATION)) ? (
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={handleAmendClick}
                disabled={
                  (purchaseCancellation.managerApproval !== null && purchaseCancellation.comexAdminApproval === null) ||
                  quotationEntity?.comexAdminDecision !== null
                    ? false
                    : true
                }
              >
                <Translate contentKey="comexApp.quotation.branchManagerApproveBtnLabel">Approve/Deny</Translate>
              </Button>
            ) : (
              <></>
            )}

            {quotationEntity?.cancelOrder === true &&
            purchaseCancellation.comexAdminApproval !== null &&
            (account?.authorities?.includes(AUTHORITIES.ALSHMOKH_ADMIN) ||
              account?.authorities?.includes(AUTHORITIES.SUBADMIN_CANCELLATION)) ? (
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" onClick={handleAmendClick}>
                {purchaseCancellation.result === 'CANCELLED' ? (
                  <Translate contentKey="home.table.detailBtn" />
                ) : (
                  <Translate contentKey="comexApp.quotation.updateDecisionBtnLabel"></Translate>
                )}
              </Button>
            ) : (
              <></>
            )}
          </div>

          <div className="col-sm-12" style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px' }}>
            {resendSmsButtons()}
          </div>

          <div className="col-sm-12" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {operationTimeError()}
          </div>
        </Grid>
      </Grid>
      <div>
        {isBoubyanQuotation(quotationEntity)
          ? <BoubyanQuotationPrint
            quotationEntity={quotationEntity}
            bankStaffAccount={bankStaffAccount}
          />
          : <QuotationPrint
            quotationEntity={quotationEntity}
            bankStaffAccount={bankStaffAccount}
          />
        }
      </div>
    </ValidatedForm>
  );
};

export default QuotationBoughtForm;
